/**
 * MMM Report Service
 * Generates standalone HTML reports with AI-generated executive summaries.
 * Reports are stored in IndexedDB (via reportDb.ts) for the report library.
 *
 * Issue: YDMA-179 §3.3
 */

import { logError } from './logger';
import { saveReport as saveReportToWorkspace, markReportUploaded } from './reportService';
import { getReportsForWorkspace, putReport, deleteReportById, searchReportsDb, MAX_REPORTS } from './reportDb';
import type { TFn } from "@/utils/channelCorrelation";

// The three modules this orchestrator combines. Their contents were lifted out
// of this file; the re-exports below keep every call site importing from here,
// which is also what keeps the page tests' `vi.mock('../../services/
// mmmReportService', ...)` factories intercepting.
import type { MMMReportContext, SavedReport } from './mmmReportShared';
// Imported, deliberately NOT re-exported: these were module-private before the
// split and the split is not a reason to widen the public surface.
import { MMM_DEFAULT_TEMPLATE, getScenarioData } from './mmmReportShared';
import { toPeriodInfo } from './mediaChatService';
import { getReportTemplate } from '../config/reportTemplates';
import { generateReportSummary } from './mmmReportSummary';
import { buildHTMLReport } from './mmmReportHtml';

export type { MMMReportContext, SavedReport } from './mmmReportShared';
export { generateReportSummary } from './mmmReportSummary';
export { buildHTMLReport } from './mmmReportHtml';

/** Re-exported so the library page can state the real cap rather than repeat the number. */
export { MAX_REPORTS };

// ============================================================
// REPORT GENERATION (combines AI + HTML)
// ============================================================

export interface GenerateReportResult {
  html: string;
  summary: string;
  reportId: string;
  /**
   * Whether the workspace's shared shelf accepted it. `false` means the report
   * exists only in this browser and the author should be told; `null` means
   * there was no workspace to share into. Never thrown — see the generator.
   */
  sharedWithWorkspace: boolean | null;
}

/**
 * Generates a complete MMM HTML report with AI summary.
 * Saves to IndexedDB for the report library.
 *
 * Issue: YDMA-179 §3.3
 */
export const generateMMMReport = async (
  ctx: MMMReportContext,
  t: TFn,
  templateKey?: string
): Promise<GenerateReportResult> => {
  // 1. Generate AI summary (with template-specific prompt suffix)
  const summary = await generateReportSummary(ctx, templateKey);

  // 2. Build HTML
  const html = buildHTMLReport(ctx, summary, t);

  // 3. Save to IndexedDB
  const scenario = getScenarioData(ctx.recommendations, ctx.selectedScenario);
  const template = templateKey ? getReportTemplate(templateKey) : MMM_DEFAULT_TEMPLATE;
  const reportId = `rpt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  // The window belongs in the title: two reports generated the same afternoon
  // on different periods were indistinguishable in the library.
  const periodTag = ctx.period ? ` — ${toPeriodInfo(ctx.period).label}` : '';
  const title = `${template.labelTh} — ${scenario?.label || ctx.selectedScenario}${periodTag} — ${new Date().toLocaleDateString('th-TH')}`;

  const report: SavedReport = {
    id: reportId,
    title,
    scenario: template.key,
    createdAt: new Date().toISOString(),
    html,
    summary,
    context: ctx,
    workspaceId: ctx.workspaceId,
  };

  // Both shelves, in this order and deliberately.
  //
  // The local write stays because it is synchronous-ish, offline-tolerant and
  // cannot fail for a permissions reason — it is the copy that guarantees the
  // author never loses a report they just paid AI credits to generate. The
  // server write is what makes it reach anyone else.
  //
  // A failed upload is logged, not thrown: the report exists, the reader has
  // it, and turning a sharing problem into a generation failure would be a
  // worse trade. The library's own migration pass picks it up next visit.
  await saveReport(report);

  let sharedWithWorkspace: boolean | null = null;
  if (ctx.workspaceId) {
    const uploaded = await saveReportToWorkspace({
      workspaceId: ctx.workspaceId,
      title,
      scenario: template.key,
      summary,
      html,
      context: ctx,
    }, t);
    sharedWithWorkspace = uploaded.success;
    if (uploaded.success) {
      // See markReportUploaded: the library sweep would otherwise post this a
      // second time. This dual-write shipped before the marker existed, so MMM
      // reports have been duplicating on the shared shelf since it did.
      markReportUploaded(reportId);
    } else {
      logError('MMMReport', 'Report saved locally but not to the workspace', uploaded.error);
    }
  }

  return { html, summary, reportId, sharedWithWorkspace };
};

/**
 * Opens a report in a new browser tab.
 */
export const openReportInNewTab = (html: string): void => {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Clean up after a delay
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// ============================================================
// REPORT LIBRARY (IndexedDB — YDMA-179 §3.3)
// ============================================================

/**
 * Get all saved reports from IndexedDB, newest first.
 */
export const getSavedReports = async (workspaceId: string): Promise<SavedReport[]> => {
  return getReportsForWorkspace(workspaceId) as Promise<SavedReport[]>;
};

/**
 * Save a report to IndexedDB.
 */
export const saveReport = async (report: SavedReport): Promise<void> => {
  return putReport(report);
};

/**
 * Delete a report by ID.
 */
export const deleteReport = async (reportId: string): Promise<void> => {
  return deleteReportById(reportId);
};

/**
 * Search saved reports by title, scenario, or summary text.
 */
export const searchReports = async (
  query: string,
  workspaceId: string,
): Promise<SavedReport[]> => {
  return searchReportsDb(query, workspaceId) as Promise<SavedReport[]>;
};

export default {
  generateMMMReport,
  generateReportSummary,
  buildHTMLReport,
  openReportInNewTab,
  getSavedReports,
  saveReport,
  deleteReport,
  searchReports,
};
